// src/utils/conservationDiagnostic.js
//
// Moteur du mini-diagnostic « Lieux & conservation » (MVP bienveillant).
// Réf. : RECOMMANDATIONS_FONCTIONNALITE_LIEUX_CONSERVATION_ARTKIV.md
//
// Principes (doc §10) :
//  - ne pas additionner mécaniquement les risques : distinguer urgence / sensibilité ;
//  - ne pas pénaliser une réponse « je ne sais pas » ;
//  - faire primer un risque immédiat observé sur le score global ;
//  - la valeur de marché n'entre JAMAIS dans la sensibilité matérielle ;
//  - dater les conclusions, ce n'est pas une certification.
//
// Le contenu (FR/EN/DE) est volontairement porté par ce module (self-contained)
// plutôt que par 300 clés i18n éparpillées. Utiliser cLang(lang) pour résoudre.

export const CONSERVATION_MODES = ["exposed", "stored"];

// ── Statuts gradués (doc §3) — palette sauge / ambre / terracotta / bordeaux ──
export const CONSERVATION_STATUS = {
  suitable:            { fg: "#5b7052", bg: "#eef2ea", border: "#d6e0cf" },
  improve:             { fg: "#8a6d2f", bg: "#faf3e2", border: "#ecdcb8" },
  monitor:             { fg: "#a85643", bg: "#f7ece7", border: "#e6cdc2" },
  professional_advice: { fg: "#8a3f4d", bg: "#f6eaec", border: "#e3c9cf" },
  incomplete:          { fg: "#6b6459", bg: "#f3f1ec", border: "#e2ddd3" },
};

const T = {
  // Libellés génériques
  title: { fr: "Prendre soin de cette œuvre", en: "Caring for this artwork", de: "Dieses Werk pflegen" },
  subtitle: {
    fr: "Quelques questions pour des conseils adaptés — chez vous comme dans un lieu spécialisé.",
    en: "A few questions for tailored advice — at home or in a specialised space.",
    de: "Ein paar Fragen für passende Hinweise — zu Hause wie an einem spezialisierten Ort.",
  },
  modeQuestion: { fr: "Comment cette œuvre est-elle conservée en ce moment ?", en: "How is this artwork kept right now?", de: "Wie wird dieses Werk derzeit aufbewahrt?" },
  answer: {
    yes: { fr: "Oui", en: "Yes", de: "Ja" },
    no: { fr: "Non", en: "No", de: "Nein" },
    unknown: { fr: "Je ne sais pas", en: "I don't know", de: "Ich weiß nicht" },
  },
  seeResult: { fr: "Voir le bilan", en: "See the summary", de: "Zusammenfassung ansehen" },
  retake: { fr: "Refaire le diagnostic", en: "Redo the check", de: "Erneut prüfen" },
  save: { fr: "Enregistrer ce bilan", en: "Save this summary", de: "Zusammenfassung speichern" },
  saving: { fr: "Enregistrement…", en: "Saving…", de: "Wird gespeichert…" },
  saved: { fr: "Bilan enregistré", en: "Summary saved", de: "Zusammenfassung gespeichert" },
  positiveHeading: { fr: "Un bon point", en: "A good point", de: "Ein Pluspunkt" },
  priorityHeading: { fr: "Un point à vérifier", en: "One thing to check", de: "Ein Punkt zum Prüfen" },
  whyHeading: { fr: "Pourquoi", en: "Why", de: "Warum" },
  mediumTipHeading: { fr: "Selon le médium", en: "For this medium", de: "Für dieses Medium" },
  professionalAdvice: {
    fr: "Compte tenu de la sensibilité de cette œuvre, l'avis d'un conservateur-restaurateur peut être utile.",
    en: "Given this artwork's sensitivity, a conservator's opinion may be helpful.",
    de: "Angesichts der Empfindlichkeit dieses Werks kann die Meinung einer Restauratorin/eines Restaurators hilfreich sein.",
  },
  disclaimer: {
    fr: "Conseils de conservation préventive, ne remplaçant pas l'examen d'un professionnel.",
    en: "Preventive conservation advice — not a substitute for a professional examination.",
    de: "Präventive Konservierungshinweise — kein Ersatz für eine fachliche Prüfung.",
  },
  lastCheck: { fr: "Dernier bilan", en: "Last summary", de: "Letzte Prüfung" },
  nextCheck: { fr: "Prochaine vérification suggérée", en: "Suggested next check", de: "Nächste empfohlene Prüfung" },
  confidence: {
    low: { fr: "confiance faible (complétez pour affiner)", en: "low confidence (add details to refine)", de: "geringe Sicherheit (ergänzen zum Verfeinern)" },
    medium: { fr: "confiance moyenne", en: "medium confidence", de: "mittlere Sicherheit" },
    high: { fr: "confiance élevée", en: "high confidence", de: "hohe Sicherheit" },
  },
  remindLabel: { fr: "Me rappeler dans", en: "Remind me in", de: "Erinnere mich in" },
  remind: {
    m3: { fr: "3 mois", en: "3 months", de: "3 Monaten" },
    m6: { fr: "6 mois", en: "6 months", de: "6 Monaten" },
    m12: { fr: "12 mois", en: "12 months", de: "12 Monaten" },
    none: { fr: "Pas de rappel", en: "No reminder", de: "Keine Erinnerung" },
  },
  dueNudge: {
    fr: "Il serait temps de refaire un point sur cette œuvre.",
    en: "It may be time to check in on this artwork again.",
    de: "Es wäre an der Zeit, dieses Werk erneut zu prüfen.",
  },
  status: {
    suitable: { fr: "Environnement adapté", en: "Suitable environment", de: "Geeignete Umgebung" },
    improve: { fr: "Une amélioration simple est possible", en: "A simple improvement is possible", de: "Eine einfache Verbesserung ist möglich" },
    monitor: { fr: "Un point mérite votre attention", en: "One point deserves attention", de: "Ein Punkt verdient Aufmerksamkeit" },
    professional_advice: { fr: "Un avis professionnel peut être utile", en: "Professional advice may help", de: "Fachlicher Rat kann helfen" },
    incomplete: { fr: "Quelques informations nous aideraient", en: "A few details would help", de: "Ein paar Angaben würden helfen" },
  },
  intro: {
    suitable: {
      fr: "Les informations renseignées indiquent un environnement adapté.",
      en: "The details provided indicate a suitable environment.",
      de: "Die Angaben deuten auf eine geeignete Umgebung hin.",
    },
    incomplete: {
      fr: "Indiquez ce que vous savez aujourd'hui, vous pourrez compléter plus tard.",
      en: "Note what you know today; you can complete it later.",
      de: "Geben Sie an, was Sie heute wissen — Sie können später ergänzen.",
    },
  },
};

export const CONSERVATION_MODE_META = {
  exposed: {
    label: { fr: "Mon œuvre est exposée", en: "My artwork is on display", de: "Mein Werk ist ausgestellt" },
    desc: { fr: "Accrochée ou posée dans un espace de vie.", en: "Hung or placed in a living space.", de: "Aufgehängt oder in einem Wohnraum platziert." },
  },
  stored: {
    label: { fr: "Mon œuvre est rangée", en: "My artwork is stored", de: "Mein Werk ist eingelagert" },
    desc: { fr: "Emballée, en caisse, sur rack ou en réserve.", en: "Packed, crated, on a rack or in storage.", de: "Verpackt, in einer Kiste, im Regal oder Lager." },
  },
};

// ── Questions (doc §4). riskAnswer = réponse qui signale un risque. ──
// severity: poids d'un flag isolé. critical: risque immédiat (prime sur le score).
export const CONSERVATION_QUESTIONS = {
  exposed: [
    {
      key: "direct_sun", riskAnswer: "yes", severity: "monitor", critical: false,
      label: { fr: "Reçoit-elle du soleil direct à un moment de la journée ?", en: "Does it get direct sunlight at some point in the day?", de: "Bekommt es tagsüber direktes Sonnenlicht?" },
      good: { fr: "Elle est à l'abri du soleil direct.", en: "It is shielded from direct sunlight.", de: "Es ist vor direktem Sonnenlicht geschützt." },
      flag: { fr: "Elle semble recevoir du soleil direct.", en: "It seems to get direct sunlight.", de: "Es scheint direktes Sonnenlicht zu bekommen." },
      action: { fr: "Décalez légèrement l'accrochage ou posez un voilage filtrant.", en: "Shift the hanging slightly or add a filtering sheer.", de: "Hängung leicht versetzen oder einen filternden Vorhang anbringen." },
      why: { fr: "La lumière cumulée peut altérer certaines couleurs, même sans soleil direct constant.", en: "Cumulative light can alter some colours, even without constant direct sun.", de: "Kumuliertes Licht kann Farben verändern, auch ohne ständige Sonne." },
    },
    {
      key: "heat_source", riskAnswer: "yes", severity: "improve", critical: false,
      label: { fr: "Est-elle proche d'un radiateur, d'une cheminée ou d'une climatisation ?", en: "Is it near a radiator, fireplace or air-conditioning?", de: "Ist es nahe einer Heizung, einem Kamin oder einer Klimaanlage?" },
      good: { fr: "Elle est éloignée d'une source de chaleur directe.", en: "It is away from a direct heat source.", de: "Es ist von einer direkten Wärmequelle entfernt." },
      flag: { fr: "Elle est proche d'une source de chaleur ou d'air.", en: "It is close to a heat or air source.", de: "Es ist nahe einer Wärme- oder Luftquelle." },
      action: { fr: "Si possible, éloignez-la de 50 cm de la source.", en: "If possible, move it 50 cm from the source.", de: "Wenn möglich, 50 cm von der Quelle entfernen." },
      why: { fr: "Les variations de chaleur et d'air assèchent et fatiguent les matériaux.", en: "Heat and airflow swings dry out and stress materials.", de: "Wärme- und Luftschwankungen trocknen Materialien aus und belasten sie." },
    },
    {
      key: "humid_room", riskAnswer: "yes", severity: "monitor", critical: false,
      label: { fr: "Est-elle dans une pièce humide ou sujette à la condensation ?", en: "Is it in a humid room or one prone to condensation?", de: "Ist es in einem feuchten oder kondensationsanfälligen Raum?" },
      good: { fr: "La pièce paraît sèche et stable.", en: "The room seems dry and stable.", de: "Der Raum wirkt trocken und stabil." },
      flag: { fr: "La pièce peut être humide.", en: "The room may be humid.", de: "Der Raum könnte feucht sein." },
      action: { fr: "Éloignez l'œuvre des murs froids ; aérez régulièrement.", en: "Move the work from cold walls; ventilate regularly.", de: "Werk von kalten Wänden wegrücken; regelmäßig lüften." },
      why: { fr: "L'humidité favorise moisissures et déformations, surtout sur papier et toile.", en: "Humidity encourages mould and warping, especially on paper and canvas.", de: "Feuchtigkeit begünstigt Schimmel und Verformung, besonders bei Papier und Leinwand." },
    },
    {
      key: "secure_fixing", riskAnswer: "no", severity: "monitor", critical: false,
      label: { fr: "Est-elle solidement fixée et adaptée à son poids ?", en: "Is it securely fixed and suited to its weight?", de: "Ist es sicher befestigt und für sein Gewicht geeignet?" },
      good: { fr: "L'accrochage est solide et adapté.", en: "The hanging is solid and suitable.", de: "Die Hängung ist stabil und geeignet." },
      flag: { fr: "La fixation mérite d'être vérifiée.", en: "The fixing is worth checking.", de: "Die Befestigung sollte geprüft werden." },
      action: { fr: "Vérifiez le support et le poids supporté par le mur.", en: "Check the fixing and the weight the wall can bear.", de: "Halterung und Wandtragfähigkeit prüfen." },
      why: { fr: "Une chute est l'accident le plus fréquent et le plus dommageable.", en: "A fall is the most frequent and most damaging accident.", de: "Ein Sturz ist der häufigste und schädlichste Unfall." },
    },
    {
      key: "high_traffic", riskAnswer: "yes", severity: "improve", critical: false,
      label: { fr: "Est-elle dans une zone de passage, à portée d'enfants ou d'animaux ?", en: "Is it in a busy area, within reach of children or pets?", de: "Ist es in einem belebten Bereich, in Reichweite von Kindern oder Tieren?" },
      good: { fr: "Elle est à l'écart des chocs et des passages.", en: "It is away from knocks and traffic.", de: "Es ist vor Stößen und Verkehr geschützt." },
      flag: { fr: "Elle est exposée aux chocs de passage.", en: "It is exposed to passing knocks.", de: "Es ist Stößen im Vorbeigehen ausgesetzt." },
      action: { fr: "Décalez-la ou protégez l'angle le plus exposé.", en: "Shift it or protect the most exposed corner.", de: "Versetzen oder die exponierteste Ecke schützen." },
      why: { fr: "Les micro-chocs répétés fragilisent cadres, angles et surfaces.", en: "Repeated micro-knocks weaken frames, corners and surfaces.", de: "Wiederholte Mikrostöße schwächen Rahmen, Ecken und Oberflächen." },
    },
    {
      key: "recent_photo", riskAnswer: "no", severity: "improve", critical: false, info: true,
      label: { fr: "Une photo récente de l'œuvre dans son environnement est-elle disponible ?", en: "Is a recent photo of the artwork in its setting available?", de: "Gibt es ein aktuelles Foto des Werks in seiner Umgebung?" },
      good: { fr: "Une photo de contexte récente existe.", en: "A recent context photo exists.", de: "Ein aktuelles Kontextfoto ist vorhanden." },
      flag: { fr: "Aucune photo de contexte récente.", en: "No recent context photo.", de: "Kein aktuelles Kontextfoto." },
      action: { fr: "Ajoutez une photo dans son environnement (privée par défaut).", en: "Add a photo in its setting (private by default).", de: "Ein Foto in der Umgebung hinzufügen (standardmäßig privat)." },
      why: { fr: "Une photo datée permet de suivre l'état de l'œuvre dans le temps.", en: "A dated photo helps track the artwork's condition over time.", de: "Ein datiertes Foto hilft, den Zustand über die Zeit zu verfolgen." },
    },
  ],
  stored: [
    {
      key: "off_ground", riskAnswer: "no", severity: "monitor", critical: false,
      label: { fr: "Est-elle surélevée par rapport au sol ?", en: "Is it raised off the floor?", de: "Steht es erhöht über dem Boden?" },
      good: { fr: "Elle est surélevée par rapport au sol.", en: "It is raised off the floor.", de: "Es steht erhöht über dem Boden." },
      flag: { fr: "Elle repose peut-être à même le sol.", en: "It may sit directly on the floor.", de: "Es steht möglicherweise direkt am Boden." },
      action: { fr: "Posez-la sur des cales ou une palette propre.", en: "Place it on blocks or a clean pallet.", de: "Auf Klötze oder eine saubere Palette stellen." },
      why: { fr: "Le contact avec le sol expose aux fuites, à la poussière et aux nuisibles.", en: "Floor contact exposes it to leaks, dust and pests.", de: "Bodenkontakt setzt es Lecks, Staub und Schädlingen aus." },
    },
    {
      key: "away_humid_wall", riskAnswer: "no", severity: "monitor", critical: false,
      label: { fr: "Est-elle éloignée d'un mur humide ou extérieur ?", en: "Is it away from a humid or external wall?", de: "Ist es von einer feuchten oder Außenwand entfernt?" },
      good: { fr: "Elle est éloignée des murs humides.", en: "It is away from humid walls.", de: "Es ist von feuchten Wänden entfernt." },
      flag: { fr: "Elle est peut-être contre un mur humide.", en: "It may be against a humid wall.", de: "Es steht möglicherweise an einer feuchten Wand." },
      action: { fr: "Laissez un espace d'air entre l'œuvre et le mur.", en: "Leave an air gap between the work and the wall.", de: "Einen Luftspalt zwischen Werk und Wand lassen." },
      why: { fr: "Les murs froids ou extérieurs condensent l'humidité au contact.", en: "Cold or external walls condense moisture on contact.", de: "Kalte oder Außenwände kondensieren bei Kontakt Feuchtigkeit." },
    },
    {
      key: "shock_protected", riskAnswer: "no", severity: "improve", critical: false,
      label: { fr: "Est-elle protégée des chocs, sans plastique au contact prolongé ?", en: "Is it shock-protected, without plastic in prolonged contact?", de: "Ist es stoßgeschützt, ohne dauerhaften Plastikkontakt?" },
      good: { fr: "Elle est protégée des chocs de façon respirante.", en: "It is shock-protected in a breathable way.", de: "Es ist atmungsaktiv stoßgeschützt." },
      flag: { fr: "La protection contre les chocs est à revoir.", en: "The shock protection needs review.", de: "Der Stoßschutz sollte überprüft werden." },
      action: { fr: "Emballez avec un matériau respirant, sans film plastique au contact direct.", en: "Wrap with a breathable material, no plastic film in direct contact.", de: "Mit atmungsaktivem Material verpacken, keine Folie im direkten Kontakt." },
      why: { fr: "Le plastique au contact piège l'humidité et peut coller à la surface.", en: "Plastic in contact traps moisture and can stick to the surface.", de: "Folie im Kontakt schließt Feuchtigkeit ein und kann an der Oberfläche haften." },
    },
    {
      key: "temp_swings", riskAnswer: "yes", severity: "monitor", critical: false,
      label: { fr: "Le lieu connaît-il de fortes variations de température ou d'humidité ?", en: "Does the place see strong temperature or humidity swings?", de: "Gibt es dort starke Temperatur- oder Feuchteschwankungen?" },
      good: { fr: "Le lieu paraît stable en température et humidité.", en: "The place seems stable in temperature and humidity.", de: "Der Ort wirkt bei Temperatur und Feuchte stabil." },
      flag: { fr: "Le lieu peut subir de fortes variations.", en: "The place may undergo strong swings.", de: "Der Ort kann starken Schwankungen unterliegen." },
      action: { fr: "Privilégiez un espace intérieur tempéré, évitez cave et grenier.", en: "Prefer a tempered indoor space; avoid cellar and attic.", de: "Bevorzugen Sie einen temperierten Innenraum; Keller und Dachboden meiden." },
      why: { fr: "Les fluctuations répétées font travailler les matériaux et créent des tensions.", en: "Repeated fluctuations make materials move and create stress.", de: "Wiederholte Schwankungen lassen Materialien arbeiten und erzeugen Spannungen." },
    },
    {
      key: "water_pest_risk", riskAnswer: "yes", severity: "monitor", critical: true,
      label: { fr: "Y a-t-il un risque de fuite d'eau, d'inondation ou de nuisibles ?", en: "Is there a risk of water leak, flooding or pests?", de: "Besteht Gefahr von Wasserleck, Überschwemmung oder Schädlingen?" },
      good: { fr: "Aucun risque d'eau ou de nuisibles identifié.", en: "No water or pest risk identified.", de: "Kein Wasser- oder Schädlingsrisiko erkennbar." },
      flag: { fr: "Un risque d'eau ou de nuisibles existe.", en: "A water or pest risk exists.", de: "Es besteht ein Wasser- oder Schädlingsrisiko." },
      action: { fr: "Éloignez l'œuvre des points d'eau et surélevez-la ; inspectez régulièrement.", en: "Move the work from water points and raise it; inspect regularly.", de: "Werk von Wasserstellen entfernen und erhöhen; regelmäßig kontrollieren." },
      why: { fr: "Une fuite ou une infestation peut causer des dommages rapides et irréversibles.", en: "A leak or infestation can cause rapid, irreversible damage.", de: "Ein Leck oder Befall kann schnelle, irreversible Schäden verursachen." },
    },
    {
      key: "visual_checks", riskAnswer: "no", severity: "improve", critical: false,
      label: { fr: "L'œuvre est-elle contrôlée visuellement de temps en temps ?", en: "Is the artwork visually checked from time to time?", de: "Wird das Werk gelegentlich sichtgeprüft?" },
      good: { fr: "Elle est contrôlée de temps en temps.", en: "It is checked from time to time.", de: "Es wird gelegentlich geprüft." },
      flag: { fr: "Elle n'est pas contrôlée régulièrement.", en: "It is not checked regularly.", de: "Es wird nicht regelmäßig geprüft." },
      action: { fr: "Prévoyez un coup d'œil tous les 3 à 6 mois.", en: "Plan a look every 3 to 6 months.", de: "Alle 3 bis 6 Monate einen Blick einplanen." },
      why: { fr: "Un contrôle régulier permet de repérer un problème avant qu'il ne s'aggrave.", en: "Regular checks catch a problem before it worsens.", de: "Regelmäßige Kontrollen erkennen ein Problem, bevor es sich verschlimmert." },
    },
  ],
};

// ── Conseils par famille de médium (doc §5) ──
export const CONSERVATION_MEDIUM_TIPS = {
  painting: { fr: "Peinture : évitez soleil direct et radiateur, et vérifiez l'accrochage.", en: "Painting: avoid direct sun and radiators, and check the hanging.", de: "Gemälde: direkte Sonne und Heizung meiden, Hängung prüfen." },
  paper: { fr: "Papier / photographie : lumière filtrée, encadrement adapté, rotation d'exposition.", en: "Paper / photography: filtered light, suitable framing, exhibition rotation.", de: "Papier / Fotografie: gefiltertes Licht, geeignete Rahmung, Ausstellungsrotation." },
  sculpture: { fr: "Sculpture : stabilisez le socle et dégagez le passage.", en: "Sculpture: stabilise the base and clear the passage.", de: "Skulptur: Sockel stabilisieren und Durchgang freihalten." },
  textile: { fr: "Textile : lumière douce, protection respirante, contrôle des nuisibles.", en: "Textile: soft light, breathable protection, pest control.", de: "Textil: sanftes Licht, atmungsaktiver Schutz, Schädlingskontrolle." },
  digital: { fr: "Vidéo / numérique : conservez plusieurs copies et documentez le matériel requis.", en: "Video / digital: keep multiple copies and document the required equipment.", de: "Video / digital: mehrere Kopien aufbewahren und die benötigte Ausrüstung dokumentieren." },
  installation: { fr: "Installation : photographiez, inventoriez et documentez le remontage.", en: "Installation: photograph, inventory and document reassembly.", de: "Installation: fotografieren, inventarisieren und den Wiederaufbau dokumentieren." },
  composite: { fr: "Objet composite : surveillez les incompatibilités entre matériaux (déformations, suintements).", en: "Composite object: watch for material incompatibilities (warping, seeping).", de: "Mischobjekt: auf Materialunverträglichkeiten achten (Verformung, Austritt)." },
  unknown: { fr: "Pour vous conseiller plus justement, précisez la technique ou ajoutez une photo.", en: "To advise you more accurately, specify the technique or add a photo.", de: "Für genauere Hinweise die Technik angeben oder ein Foto hinzufügen." },
};

// medium_family_key (taxonomie médium) → famille du diagnostic
export function conservationMediumFamily(mediumFamilyKey) {
  const k = String(mediumFamilyKey || "").toLowerCase();
  if (["painting"].includes(k)) return "painting";
  if (["photography", "work_on_paper", "works_on_paper", "drawing", "prints"].includes(k)) return "paper";
  if (["sculpture", "ceramics"].includes(k)) return "sculpture";
  if (["textile"].includes(k)) return "textile";
  if (["film_video", "digital", "nft"].includes(k)) return "digital";
  if (["installation"].includes(k)) return "installation";
  if (!k || k === "other" || k === "unknown") return "unknown";
  return "composite";
}

export function cLang(lang) {
  const l = String(lang || "fr").slice(0, 2);
  return ["fr", "en", "de"].includes(l) ? l : "fr";
}
export function cText(obj, lang) {
  if (!obj) return "";
  return obj[cLang(lang)] || obj.fr || "";
}
export const CONSERVATION_UI = T;

// ── Le moteur (doc §10) ──
export function evaluateConservation({ mode, answers = {}, mediumFamilyKey }) {
  const questions = CONSERVATION_QUESTIONS[mode] || [];
  const family = conservationMediumFamily(mediumFamilyKey);

  const riskFlags = [];
  const positives = [];
  let answered = 0;

  for (const q of questions) {
    const a = answers[q.key];
    if (a !== "yes" && a !== "no") continue; // "unknown"/absent : non pénalisant
    answered += 1;
    if (a === q.riskAnswer) riskFlags.push(q);
    else positives.push(q);
  }

  const total = questions.length || 1;
  const ratio = answered / total;
  const confidence = ratio >= 0.8 ? "high" : ratio >= 0.4 ? "medium" : "low";

  // Trop peu d'éléments → « à compléter », sans verdict.
  if (answered < 2) {
    return {
      status: "incomplete", confidence, riskCount: riskFlags.length,
      professionalAdvice: false,
      positive: positives[0] || null, priority: null, mediumFamily: family,
    };
  }

  const criticalFlags = riskFlags.filter((q) => q.critical);
  const sensitive = ["paper", "textile", "composite"].includes(family);

  let status;
  if (riskFlags.length === 0) status = "suitable";
  else if (criticalFlags.length >= 1 || riskFlags.length >= 2) status = "monitor";
  else status = "improve"; // exactement 1 flag non critique

  // Avis professionnel — conservateur (doc §7), déclenché à bon escient uniquement.
  let professionalAdvice = false;
  if (riskFlags.length >= 3) professionalAdvice = true;
  if (criticalFlags.length >= 1 && sensitive) professionalAdvice = true;
  if (professionalAdvice) status = "professional_advice";

  // Priorité = flag le plus sévère (critique > monitor > 1er).
  const priority =
    criticalFlags[0] ||
    riskFlags.find((q) => q.severity === "monitor") ||
    riskFlags[0] ||
    null;

  return {
    status,
    confidence,
    riskCount: riskFlags.length,
    professionalAdvice,
    positive: positives[0] || null,
    priority,
    mediumFamily: family,
  };
}

// Date suggérée du prochain contrôle (doc §8.7) : +months mois.
export function nextCheckDate(months = 6, from = new Date()) {
  const d = new Date(from);
  d.setMonth(d.getMonth() + Number(months || 6));
  return d.toISOString().slice(0, 10);
}
